import Paper from '@material-ui/core/Paper'
import { useState, useCallback, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import deburr from 'lodash/deburr'
import { Controller, useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'

import fetchAfterSale from '../../../../domain/features/afterSale/fetchAfterSale'
import normalizeAfterSaleForm from '../../../../domain/features/afterSale/normalizeAfterSale'
import {
  afterSaleSelector,
  resetAfterSale
} from '../../../../domain/features/afterSale/afterSaleSlice'

import SelectMui from '@material-ui/core/Select'
import IconButton from '@material-ui/core/IconButton'
import InputLabel from '@material-ui/core/InputLabel'
import FormControl from '@material-ui/core/FormControl'
import MenuItem from '@material-ui/core/MenuItem'
import Button from '@material-ui/core/Button'
import FormHelperText from '@material-ui/core/FormHelperText'

import Modal from '../../atomics/Modal/Modal'
import Text from '../../atomics/Typography/Text'
import Title from '../../atomics/Typography/Title'
import Input from '../../atomics/Input/Input'
import Row from '../../atomics/Styles/Row'
import SubmitButton from '../../atomics/Button/SubmitButton/SubmitButton'
import { reasonList } from './typesAfterSale'
import ButtonDump from '../../atomics/Button/ButtonDump'
import WithdrawItem from './WithdrawItem'

// Styled components
const SubmitButtonAdd = styled(SubmitButton)`
  width: 111px;
  height: 36px;
`

const ButtonItem = styled(Button)`
  font-size: 14px;
  line-height: 18px;
  letter-spacing: 0.2px;
  color: #5c5f62;
  text-transform: none;
  @media (min-width: 1100px) {
    width: 206px;
  }
  &:disabled {
    cursor: not-allowed;
    pointer-events: auto;
  }
`

const Label = styled(InputLabel)`
  padding-left: 5px;
  padding-right: 5px;
  transform: translate(14px, 10px) scale(1);
  background-color: ${({ theme }) => theme.palette.background.default};
`

const RowCenter = styled(Row)`
  align-items: center;
  margin-right: 2rem;
`

const Select = styled(SelectMui)`
  height: 36px;
  margin-right: 1rem;
  border-color: ${({ theme }) =>
    theme.palette.type === 'dark' ? '#FFFFFF' : '#b7b7b7'};
  color: ${({ theme }) =>
    theme.palette.type === 'dark' ? '#FFFFFF' : '#202223'};
  .MuiSelect-outlined {
    padding-right: 14px;
    border-color: ${({ theme }) =>
      theme.palette.type === 'dark' ? '#FFFFFF' : '#b7b7b7'};
  }
`

const Form = styled('form')`
  display: flex;
  flex-direction: column;
  flex: 1;
  @media (min-width: 1100px) {
    padding-right: 1rem;
    padding-left: 1rem;
  }
`

const ModalContainer = styled(Modal)`
  @media (max-width: 1100px) {
    margin: 1rem;
  }
`

const TitleModal = styled(Title)`
  font-weight: 500;
  font-size: 24px;
  line-height: 28px;
  text-align: center;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`

const Container = styled(Paper)`
  display: flex;
  flex-direction: column;
  padding: 1.5rem;
  padding-top: 0.5rem;
  background-color: ${({ theme }) => theme.palette.background.default};
  box-shadow: 0px 0px 8px rgba(0, 0, 0, 0.15);
  border-radius: 10px;
  max-height: 90vh;
  overflow-y: auto;

  @media (min-width: 1100px) {
    width: 758px;
    padding-left: 4rem;
  }
  @media (max-width: 1100px) {
    width: auto;
  }
`

const Divider = styled('div')`
  height: 0.8rem;
  width: 2rem;
`

const Empty = styled('div')`
  height: 1rem;
  width: 1rem;
  margin-left: 1.8rem;
`

const Head = styled('div')`
  display: flex;
  flex: 1;
  margin-bottom: 2rem;
`

const Footer = styled('div')`
  display: flex;
  flex: 1;
  justify-content: flex-end;
  margin-right: 1rem;
`

const Limit = styled('div')`
  border: 1px solid #b7b7b7;
  transform: rotate(90deg);
  width: 40px;
  height: 0px;
`

const FieldQuantity = styled(Input)`
  height: auto;
  .MuiInputBase-input {
    min-width: 100%;
  }
`

/**
 * Individual item row component
 */
const ItemRow = ({
  item,
  index,
  control,
  allItems,
  selectedItemIds,
  onItemChange,
  onQuantityChange,
  onRemove,
  canRemove
}) => {
  // Get available options for this dropdown:
  // - Current selection (always shown)
  // - Items not selected in other rows
  const availableOptions = useMemo(() => {
    return allItems.filter(({ value }) => {
      const isCurrentSelection = String(value) === String(item.value)
      const isSelectedElsewhere = selectedItemIds.some(
        (id, idx) => idx !== index && String(id) === String(value)
      )
      return isCurrentSelection || !isSelectedElsewhere
    })
  }, [allItems, item.value, selectedItemIds, index])

  // Get current item data for price display and max quantity
  const currentItemData = useMemo(() => {
    return allItems.find(i => String(i.value) === String(item.value)) || item
  }, [allItems, item])

  return (
    <RowCenter>
      {/* Item Select */}
      <Controller
        name={`listItem.${index}.value`}
        control={control}
        rules={{ required: 'Seleccione un item' }}
        render={({ field, fieldState: { error } }) => (
          <FormControl variant="outlined" fullWidth error={!!error}>
            <Label>Item</Label>
            <Select
              {...field}
              value={field.value || ''}
              onChange={e => {
                field.onChange(e)
                onItemChange(index, e.target.value)
              }}
            >
              {availableOptions.map(({ value, name }) => (
                <MenuItem key={String(value)} value={value}>
                  {name}
                </MenuItem>
              ))}
            </Select>
            {error && <FormHelperText>{error.message}</FormHelperText>}
          </FormControl>
        )}
      />

      {/* Quantity Input */}
      <Controller
        name={`listItem.${index}.quantity`}
        control={control}
        rules={{
          required: 'Cantidad requerida',
          validate: {
            positive: v =>
              parseInt(v, 10) > 0 || 'Debe ser mayor a 0',
            withinLimit: v =>
              parseInt(v, 10) <= (currentItemData?.quantity || 9999) ||
              `Máximo: ${currentItemData?.quantity || 9999}`
          }
        }}
        render={({ field, fieldState: { error } }) => (
          <FieldQuantity
            label="Cantidad"
            type="number"
            inputMode="numeric"
            error={!!error}
            helperText={error?.message}
            InputProps={{
              inputProps: {
                min: 1,
                max: currentItemData?.quantity || 1
              }
            }}
            {...field}
            value={field.value || ''}
            onChange={e => {
              field.onChange(e)
              onQuantityChange(index, e.target.value)
            }}
          />
        )}
      />

      <Divider />
      <Divider />

      {/* Price Display */}
      <Text>
        ${Number(currentItemData?.price || 0).toLocaleString('es-CL')}
      </Text>

      {/* Remove Button */}
      {canRemove ? (
        <>
          <Limit />
          <ButtonDump onClick={() => onRemove(index)} />
        </>
      ) : (
        <Empty />
      )}
    </RowCenter>
  )
}

/**
 * Main Modal Component
 */
const ModalAfterSale = ({
  title,
  headerText,
  orderType,
  id,
  fieldArray = [],
  open,
  onClose,
  ...props
}) => {
  const dispatch = useDispatch()
  const { status: statusForm, errorMessage } = useSelector(afterSaleSelector)

  // Local state for items (source of truth for item management)
  const [items, setItems] = useState(() =>
    fieldArray.map(item => ({
      value: item.value,
      quantity: item.quantity,
      name: item.name,
      price: item.price
    }))
  )

  // Reset items when fieldArray changes or modal opens
  useEffect(() => {
    if (open) {
      setItems(
        fieldArray.map(item => ({
          value: item.value,
          quantity: item.quantity,
          name: item.name,
          price: item.price
        }))
      )
    }
  }, [fieldArray, open])

  // Get list of selected item IDs for filtering dropdowns
  const selectedItemIds = useMemo(
    () => items.map(item => item.value),
    [items]
  )

  // Get available items that haven't been added yet
  const availableToAdd = useMemo(() => {
    return fieldArray.filter(
      item => !selectedItemIds.includes(item.value)
    )
  }, [fieldArray, selectedItemIds])

  // Form setup
  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isValid },
    reset,
    setValue,
    trigger
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      reason: '',
      listItem: items.map(item => ({
        value: item.value,
        quantity: 1
      })),
      transport: 0,
      penalty: 0,
      isWithdrawable: false
    }
  })

  // Filter reasons by order type
  const filteredReasons = useMemo(
    () => reasonList.filter(({ type }) => deburr(type) === deburr(orderType)),
    [orderType]
  )

  // Sync form when items change
  useEffect(() => {
    setValue(
      'listItem',
      items.map(item => ({
        value: item.value,
        quantity: 1
      })),
      { shouldValidate: true }
    )
  }, [items, setValue])

  // Reset form when modal opens
  useEffect(() => {
    if (open && statusForm !== 'loading') {
      reset({
        reason: filteredReasons[0]?.id || '',
        listItem: fieldArray.map(item => ({
          value: item.value,
          quantity: 1
        })),
        transport: 0,
        penalty: 0,
        isWithdrawable: false
      })
      trigger()
    }
  }, [open, reset, fieldArray, statusForm, filteredReasons, trigger])

  // Handle item selection change
  const handleItemChange = useCallback(
    (index, newValue) => {
      setItems(prev => {
        const newItems = [...prev]
        const newItemData = fieldArray.find(
          i => String(i.value) === String(newValue)
        )
        if (newItemData) {
          newItems[index] = { ...newItemData }
        }
        return newItems
      })
      // Reset quantity to 1 when item changes
      setValue(`listItem.${index}.quantity`, 1, { shouldValidate: true })
    },
    [fieldArray, setValue]
  )

  // Handle quantity change
  const handleQuantityChange = useCallback(() => {
    // La cantidad la gestiona react-hook-form vía field.onChange en ItemRow.
    // No actualizar items state aquí evita que el useEffect de sync
    // resetee las cantidades a 1 cada vez que el usuario edita el campo.
  }, [])

  // Add new item
  const handleAddItem = useCallback(() => {
    if (availableToAdd.length > 0) {
      const itemToAdd = availableToAdd[0]
      setItems(prev => [...prev, { ...itemToAdd }])
    }
  }, [availableToAdd])

  // Remove item
  const handleRemoveItem = useCallback(
    index => {
      setItems(prev => prev.filter((_, i) => i !== index))
      // Update form array
      setValue(
        'listItem',
        items
          .filter((_, i) => i !== index)
          .map(item => ({
            value: item.value,
            quantity: 1
          })),
        { shouldValidate: true }
      )
    },
    [items, setValue]
  )

  // Submit handler
  const onSubmit = useCallback(
    data => {
      const payload = normalizeAfterSaleForm({
        ...data,
        id,
        status:
          orderType === 'Cancelación'
            ? 'Cancelado'
            : orderType === 'Reenvío'
              ? 'No Despachado'
              : 'Ingresado',
        type: orderType
      })
      dispatch(fetchAfterSale(payload))
    },
    [dispatch, id, orderType]
  )

  // Close modal on success
  useEffect(() => {
    if (statusForm === 'finished') {
      onClose()
      dispatch(resetAfterSale())
    }
  }, [statusForm, onClose, dispatch])

  return (
    <ModalContainer open={open} onClose={onClose} {...props} disableAutoFocus>
      <Container elevation={2}>
        <IconButton aria-label="close" onClick={onClose} />

        <Head>
          <TitleModal>{title}</TitleModal>
        </Head>

        <Text>{headerText}</Text>
        <Divider />
        <Divider />

        <Form onSubmit={handleSubmit(onSubmit)}>
          {/* Reason Select */}
          <Controller
            name="reason"
            control={control}
            rules={{ required: 'Seleccione un motivo' }}
            render={({ field, fieldState: { error } }) => (
              <FormControl variant="outlined" fullWidth error={!!error}>
                <Label>Motivo</Label>
                <Select {...field} value={field.value || ''}>
                  {filteredReasons.map(({ id: reasonId, view }) => (
                    <MenuItem key={reasonId} value={reasonId}>
                      {view}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{error?.message}</FormHelperText>
              </FormControl>
            )}
          />

          <Divider />

          {/* Item Rows */}
          {items.map((item, index) => (
            <ItemRow
              key={`item-${item.value}-${index}`}
              item={item}
              index={index}
              control={control}
              allItems={fieldArray}
              selectedItemIds={selectedItemIds}
              onItemChange={handleItemChange}
              onQuantityChange={handleQuantityChange}
              onRemove={handleRemoveItem}
              canRemove={items.length > 1}
            />
          ))}

          {/* Add Item Button */}
          {availableToAdd.length > 0 && (
            <ButtonItem onClick={handleAddItem}>
              + Agregar otro item
            </ButtonItem>
          )}

          {/* Transport Input */}
          <Controller
            name="transport"
            control={control}
            rules={{
              validate: v =>
                parseInt(v, 10) >= 0 || 'Debe ser mayor o igual a 0'
            }}
            render={({ field, fieldState: { error } }) => (
              <Input
                type="number"
                label="Transporte"
                fullWidth
                inputMode="numeric"
                InputProps={{ inputProps: { min: 0 } }}
                error={!!error}
                helperText={error?.message}
                {...field}
                value={field.value ?? 0}
              />
            )}
          />

          <Divider />

          {/* Penalty Input */}
          <Controller
            name="penalty"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Input
                type="number"
                inputMode="numeric"
                label="Multa"
                fullWidth
                error={!!error}
                helperText={error?.message}
                {...field}
                value={field.value ?? 0}
              />
            )}
          />

          <Divider />
          <Divider />

          {/* Withdraw Checkbox (only for Reenvío) */}
          {orderType === 'Reenvío' && (
            <Text>
              <WithdrawItem
                control={control}
                name="isWithdrawable"
                defaultValue={false}
              />
            </Text>
          )}

          <Divider />

          {/* Error Message */}
          {statusForm === 'error' && (
            <FormHelperText error>
              {errorMessage || 'Ocurrió un error, intente más tarde.'}
            </FormHelperText>
          )}

          {/* Submit Button */}
          <Footer>
            <SubmitButtonAdd
              type="submit"
              disabled={isSubmitting || !isValid || statusForm === 'loading'}
              isLoading={statusForm === 'loading'}
            >
              Agregar
            </SubmitButtonAdd>
          </Footer>
        </Form>
      </Container>
    </ModalContainer>
  )
}

export default ModalAfterSale
